<script setup lang="ts">
import {
  SUGGESTION_CATEGORIES,
  SUGGESTION_STATUSES,
  SUGGESTIONS_PAGE_SIZE,
  isSuggestionCategory,
  isSuggestionStatus,
  type Suggestion,
  type SuggestionSort,
  type SuggestionVoteResult
} from '~~/shared/utils/suggestions'

const route = useRoute()
const router = useRouter()
const user = useSupabaseUser()
const authStore = useAuthStore()
const overlay = useOverlay()

const title = 'Suggestions: vote on what mychar.gg builds next'
const description = 'Suggest features for mychar.gg and vote on ideas from other users. The most upvoted suggestions rise to the top, and each one shows where it stands.'

useSeoMeta({
  title,
  titleTemplate: null,
  ogTitle: title,
  description,
  ogDescription: description
})

defineOgImageComponent('Saas', {
  title: 'Suggestions',
  description: 'Suggest features and vote on what mychar.gg builds next.'
})

const siteUrl = useRuntimeConfig().public.siteUrl
useSchemaOrg([
  defineWebPage({ '@type': 'CollectionPage' }),
  defineBreadcrumb({
    itemListElement: [
      { name: 'Help center', item: `${siteUrl}/help` },
      { name: 'Suggestions', item: `${siteUrl}/suggestions` }
    ]
  })
])

// Filters live in the query string so every view is shareable and survives reloads
const filters = computed(() => ({
  category: isSuggestionCategory(route.query.category) ? route.query.category : undefined,
  status: isSuggestionStatus(route.query.status) ? route.query.status : undefined,
  sort: (route.query.sort === 'new' ? 'new' : 'top') as SuggestionSort,
  page: Math.max(1, Number(route.query.page) || 1)
}))

const hasFilters = computed(() => !!(filters.value.category || filters.value.status))

/** Merge into the query; any filter change goes back to page 1. */
function setQuery(patch: Record<string, string | undefined>) {
  router.replace({ query: { ...route.query, page: undefined, ...patch } })
}

const sort = computed({
  get: () => filters.value.sort,
  set: (value: SuggestionSort) => setQuery({ sort: value === 'top' ? undefined : value })
})

const status = computed({
  get: () => filters.value.status ?? 'all',
  set: (value: string) => setQuery({ status: value === 'all' ? undefined : value })
})

const sortTabs = [
  { label: 'Top', value: 'top', icon: 'i-lucide-trending-up' },
  { label: 'New', value: 'new', icon: 'i-lucide-clock' }
]

const statusItems: { label: string, value: string, icon?: string }[] = [
  { label: 'Any status', value: 'all' },
  ...SUGGESTION_STATUSES.map(s => ({ label: s.label, value: s.value, icon: s.icon }))
]
const statusIcon = computed(() => statusItems.find(s => s.value === status.value)?.icon ?? 'i-lucide-list-filter')

// useFetch forwards cookies during SSR, so the viewer's own votes render server-side
const { data, status: fetchStatus, error, refresh } = await useFetch<{ suggestions: Suggestion[], total: number }>('/api/suggestions', {
  query: computed(() => ({ ...filters.value, limit: SUGGESTIONS_PAGE_SIZE }))
})

const suggestions = computed(() => data.value?.suggestions ?? [])
const total = computed(() => data.value?.total ?? 0)
const refreshing = computed(() => fetchStatus.value === 'pending' && !!data.value)

// Data is a shallow ref: replace the row instead of mutating it. Rows keep
// their place until the next load, so nothing jumps under the cursor.
function applyVote(id: string, result: SuggestionVoteResult) {
  if (!data.value) return
  data.value = {
    ...data.value,
    suggestions: data.value.suggestions.map(s => (s.id === id ? { ...s, ...result } : s))
  }
}

async function openEditor() {
  if (!user.value?.sub) {
    await navigateTo(loginPathFor(route.fullPath))
    return
  }
  if (authStore.character && !authStore.character.username) {
    await navigateTo('/create-username')
    return
  }

  const SuggestionEditorModal = defineAsyncComponent(() => import('~/components/modals/suggestions/SuggestionEditorModal.vue'))
  const id = await overlay.create(SuggestionEditorModal).open({ category: filters.value.category }).result
  if (id) await navigateTo(`/suggestions/${id}`)
}
</script>

<template>
  <div class="min-h-screen">
    <UContainer class="pb-24 pt-28 sm:pt-32">
      <UBreadcrumb
        :items="[{ label: 'Help center', to: '/help' }, { label: 'Suggestions', to: '/suggestions' }]"
        class="mb-4"
      />

      <!-- Header -->
      <div class="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 class="text-3xl font-bold text-highlighted sm:text-4xl">
            Suggestions
          </h1>
          <p class="mt-2 text-muted">
            Share ideas for mychar.gg and vote on the ones you want most.
          </p>
        </div>
        <UButton
          label="New suggestion"
          icon="i-lucide-plus"
          color="primary"
          class="self-start sm:self-auto"
          @click="openEditor"
        />
      </div>

      <!-- Category filter -->
      <div class="-mx-4 mb-3 flex gap-1.5 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
        <UButton
          label="All"
          icon="i-lucide-layout-list"
          color="neutral"
          :variant="!filters.category ? 'subtle' : 'ghost'"
          size="sm"
          class="shrink-0"
          @click="setQuery({ category: undefined })"
        />
        <UButton
          v-for="c in SUGGESTION_CATEGORIES"
          :key="c.value"
          :label="c.label"
          :icon="c.icon"
          color="neutral"
          :variant="filters.category === c.value ? 'subtle' : 'ghost'"
          size="sm"
          class="shrink-0"
          @click="setQuery({ category: c.value })"
        />
      </div>

      <!-- Sort + status -->
      <div class="mb-5 flex items-center justify-between gap-3">
        <UTabs
          v-model="sort"
          :items="sortTabs"
          :content="false"
          size="sm"
        />
        <USelect
          v-model="status"
          :items="statusItems"
          :icon="statusIcon"
          value-key="value"
          size="sm"
          class="w-40"
          aria-label="Filter by status"
        />
      </div>

      <!-- List -->
      <div
        v-if="error && !data"
        class="rounded-2xl border border-default p-10 text-center"
      >
        <p class="text-muted">
          Could not load suggestions.
        </p>
        <UButton
          label="Try again"
          color="neutral"
          variant="outline"
          size="sm"
          class="mt-4"
          @click="refresh()"
        />
      </div>

      <div
        v-else-if="suggestions.length"
        class="space-y-3 transition-opacity duration-200"
        :class="{ 'opacity-60': refreshing }"
      >
        <SuggestionListItem
          v-for="suggestion in suggestions"
          :key="suggestion.id"
          :suggestion="suggestion"
          @voted="applyVote(suggestion.id, $event)"
        />
      </div>

      <div
        v-else
        class="rounded-2xl border border-dashed border-default p-10 text-center"
      >
        <UIcon
          name="i-lucide-lightbulb"
          class="size-6 text-dimmed"
        />
        <p class="mt-2 text-muted">
          {{ hasFilters ? 'No suggestions match these filters.' : 'No suggestions yet. Be the first to share an idea.' }}
        </p>
        <UButton
          v-if="hasFilters"
          label="Clear filters"
          color="neutral"
          variant="outline"
          size="sm"
          class="mt-4"
          @click="setQuery({ category: undefined, status: undefined })"
        />
      </div>

      <div
        v-if="total > SUGGESTIONS_PAGE_SIZE"
        class="mt-10 flex justify-center"
      >
        <UPagination
          :page="filters.page"
          :items-per-page="SUGGESTIONS_PAGE_SIZE"
          :total="total"
          :to="(p: number) => ({ query: { ...route.query, page: p > 1 ? p : undefined } })"
        />
      </div>
    </UContainer>
  </div>
</template>
